import { motion } from "framer-motion";
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { Facebook, Linkedin, Instagram } from 'lucide-react';
import VerticalLinesBackground from "@/components/ui/VerticalLinesBackground";

const GetInTouch = () => {
  const { ref: sectionRef, isInView } = useScrollAnimation();

  return (
    <section ref={sectionRef} id="get-in-touch" className="relative bg-[#11100c] overflow-hidden">
      {/* Background Lines */}
      <VerticalLinesBackground lines={4} color="#2BB680" style={{opacity: isInView ? 0.1 : 0, transition: 'opacity 1s'}} />

      {/* Main Content */}
      <div className="relative w-full flex flex-col pb-24">
        <div className="flex flex-col h-full">
          {/* Title in the top left */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: isInView ? 1 : 0, x: isInView ? 0 : -30 }}
            transition={{ duration: 0.6 }}
            className="absolute top-0 left-0 pt-8 sm:pt-24 pl-4 sm:pl-24"
          >
            <p className="text-[#2BB680] text-base sm:text-lg font-nexa-bold relative-parent contact-us-before">
              GET IN TOUCH
            </p>
          </motion.div>

          <div className="flex flex-col lg:flex-row w-full px-4 sm:px-24 pt-[120px] sm:pt-[280px] pb-8 sm:pb-24 flex-1">
            {/* Left: Title */}
            <motion.div 
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 50 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex-1 flex flex-col mb-8 lg:mb-0"
            >
              <h2 className="text-white font-nexa-bold lg:text-[4rem] sm:text-[3rem] leading-[1.2] sm:leading-[0.95] uppercase sm:ml-24 mb-8 sm:mb-0">
                <motion.span
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 20 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  className="block"
                >
                  LET'S CONNECT
                </motion.span>
                <motion.span
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 20 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                  className="block"
                >
                  IN PERSON—MEET
                </motion.span>
                <motion.span
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 20 }}
                  transition={{ duration: 0.5, delay: 1 }}
                  className="block"
                >
                  US OFFLINE.
                </motion.span>
              </h2>
            </motion.div>

            {/* Right: Info */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: isInView ? 1 : 0, x: isInView ? 0 : 50 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col w-full lg:w-1/3 gap-8"
            >
              {/* Office Info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.2 }}
              >
                <h3 className="text-xs text-[#8b8b8b] font-nexa-bold mb-3 tracking-widest uppercase">
                  OFFICE
                </h3>
                <a href="https://maps.app.goo.gl/4F2Tv3JgjdDESD3T8" target="_blank" rel="noopener noreferrer" className="text-sm text-white font-nexa-book leading-relaxed hover:text-[#2BB680] transition-colors block">
                  الفيوم الجديدة
                  <br />
                  المنطقة الصناعية الثانية قطعة رقم ٢٨
                </a>
              </motion.div>

              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.4 }}
              >
                <h3 className="text-xs text-[#8b8b8b] font-nexa-bold mb-3 tracking-widest uppercase">
                  GET IN TOUCH
                </h3>
                <a href="tel:+201068002004" className="text-sm text-white font-nexa-book mb-2 block hover:text-[#2BB680] transition-colors">
                  +20 106 800 2004
                </a>
                <a href="tel:+201095590767" className="text-sm text-white font-nexa-book mb-2 block hover:text-[#2BB680] transition-colors">
                  +20 109 559 0767
                </a>
                <a href="tel:+201285552510" className="text-sm text-white font-nexa-book mb-2 block hover:text-[#2BB680] transition-colors">
                  +20 128 555 2510
                </a>
                <a href="mailto:info@ahsilicone.com" className="text-sm text-white font-nexa-book block hover:text-[#2BB680] transition-colors">
                  info@ahsilicone.com
                </a>
              </motion.div>

              {/* Social Links */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.6 }}
              >
                <h3 className="text-xs text-[#8b8b8b] font-nexa-bold mb-3 tracking-widest uppercase">
                  SOCIAL
                </h3>
                <div className="flex gap-4 mt-2">
                  <a
                    href="https://www.instagram.com/ahsilicone"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-[#2BB680] transition-colors"
                  >
                    <Instagram className="w-4 h-4 text-black hover:text-white transition-colors" />
                  </a>
                  <a
                      href="https://shorturl.at/Idwcc"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-[#2BB680] transition-colors"
                  >
                    <svg
                      className="w-4 h-4 text-black hover:text-white transition-colors"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M17 2.05V4.1c0 .55-.45 1-1 1h-2c-.55 0-1 .45-1 1v2h4l-.5 4h-3.5v8h-4v-8H5V8.1h2.5V6.1C7.5 4.01 9.51 2 11.6 2h2.4c.55 0 1 .45 1 1z" />
                    </svg>
                  </a>
                  <a
                   href="https://www.linkedin.com/company/ahforindustries"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                    className="w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-[#2BB680] transition-colors"
                  >
                    <svg
                      className="w-4 h-4 text-black hover:text-white transition-colors"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M19 0h-14c-2.76 0-5 2.24-5 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5v-14c0-2.76-2.24-5-5-5zm-11 19h-3v-9h3v9zm-1.5-10.28c-.97 0-1.75-.79-1.75-1.75s.78-1.75 1.75-1.75 1.75.79 1.75 1.75-.78 1.75-1.75 1.75zm13.5 10.28h-3v-4.5c0-1.08-.02-2.47-1.5-2.47-1.5 0-1.73 1.17-1.73 2.39v4.58h-3v-9h2.88v1.23h.04c.4-.75 1.38-1.54 2.84-1.54 3.04 0 3.6 2 3.6 4.59v4.72z" />
                    </svg>
                  </a>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GetInTouch;
